/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BaseComponent } from '../../../../../core/components';
import { ColouriseService, LogService } from '../../../../../core/services';
import { Component } from '@angular/core';
import { ErrorResponseModel } from '../../../../../core/models';
import { GslbApiService } from '../../../shared/http';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-gslb-reports',
    templateUrl: './gslb-reports.component.html',
    styleUrls: ['./gslb-reports.component.scss']
})
export class GslbReportsComponent extends BaseComponent {
    /**
     * To store the data.
     */
    public data: object;

    /**
     * To store the title.
     */
    public title: string;

    /**
     * To store the error message.
     */
    public errorMessage: string;

    constructor(
        private colouriseService: ColouriseService,
        private gslbApiService: GslbApiService,
        private logService: LogService,
        private route: ActivatedRoute,
        private router: Router,
    ) {
        super();

        this.listenForRouteChange();
    }

    /**
     * Listen for changes in the route when switching between Health Check and Distribution State.
     * @private
     */
    private listenForRouteChange(): void {
        this.router.events.subscribe((val) => {
            if (val instanceof NavigationEnd) {
                this.getPath();
            }
        });
    }

    /**
     * Get GSLB sub-route.
     * @private
     */
    private getPath(): void {
        let path: string;
        this.route.params.subscribe((params) => {
            path = params['id'];
        });

        this.handleGSLBReporting(path);
    }

    /**
     * Determine which report to show to user based on path.
     * @param path
     * @private
     */
    private handleGSLBReporting(path: string): void {
        switch (path) {
            case 'healthcheck':
                this.getReportData('gslb_report_health_check', 'getHealthChecksReport');
                break;
            case 'distributionstate':
                this.getReportData('gslb_report_distribution', 'getDistributionStateReport');
                break;
            default:
                break;
        }
    }

    /**
     * Get report data.
     * @param title
     * @param apiCall
     * @private
     */
    private getReportData(title: string, apiCall: any): void {
        this.title = title;
        this.gslbApiService[apiCall]()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    if (Object.keys(data).length) {
                        const convertedData = this.colouriseService.convertDataToString(data);
                        const response = {
                            type: 'file',
                            data: convertedData
                        };
                        this.data = this.colouriseService.displayDataInView(response);
                    } else {
                        this.showErrorMessage(this.title);
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('GslbReportsComponent: getReportData()', error.message);
                    this.showErrorMessage(this.title);
                }
            })
            .add((): void => {
                this.showErrorMessage(this.title);
            });
    }

    /**
     * Display error message to user.
     * @param report
     * @private
     */
    private showErrorMessage(report: string): string {
        switch (report) {
            case 'gslb_report_distribution':
                this.errorMessage = 'gslb_report_distribution_error';
                break;
            case 'gslb_report_health_check':
                this.errorMessage = 'gslb_report_health_check_error';
                break;
            default:
                break;
        }

        return this.errorMessage;
    }
}
